import React, { useEffect, useRef, useState } from "react";
import CombineSharingTreeMembers from "@/components/CombineSharingTreeMembers";
import SharingTreeRosterReview from "@/components/SharingTreeRosterReview";
import { saveSharingTreeRoster } from "@/lib/sharing-tree-roster";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canUseStaffPage } from "@/lib/page-permissions";
import { hasClientPrivacy, hiddenClientFields } from "@/lib/client-privacy";
import { TreePine, Plus, X, Gift, CreditCard, UsersIcon, FileDown, Printer, AlertTriangle, Trash2, CalendarClock, Mail, CheckCircle, ChevronDown, ChevronUp, ExternalLink, GitMerge, Monitor } from "lucide-react";
import DistributionCallout from "@/components/DistributionCallout";
import { formatPhone, parseAddress } from "@/lib/utils";
import ActivitySection from "@/components/ActivitySection";
import AddressFields from "@/components/AddressFields";
import SharingTreeEmailBatchModal from "@/components/SharingTreeEmailBatchModal";
import SharingTreeToyFollowups from "@/components/SharingTreeToyFollowups";
import SharingTreeMailer from "@/components/SharingTreeMailer";
import SharingTreePostcard from "@/components/SharingTreePostcard";
import AveryLabelPrintModal from "@/components/AveryLabelPrintModal";
import ToyRequestField, { normalizeToyFields, toyPayload } from "@/components/ToyRequestField";
import useToySuggestions from "@/hooks/useToySuggestions";
import ToySuggestionCatalog from "@/components/ToySuggestionCatalog";
import SharingTreeAdditionalInformation from "@/components/SharingTreeAdditionalInformation";
import GiftCardsByHousehold from "@/components/GiftCardsByHousehold";
import ReportTableScroll from "@/components/ReportTableScroll";
import EnrolledHouseholdsCard from "@/components/EnrolledHouseholdsCard";
import HouseholdContactDetails from "@/components/HouseholdContactDetails";
import DistributionChecklist, { distributionScope } from "@/components/DistributionChecklist";
import ToyRequestReview from "@/components/ToyRequestReview";
import { CLOTHING_SIZES } from "@/lib/sharing-tree-clothing";
import { foodVoucherTotal } from "@/lib/sharing-tree-vouchers";

const EMPTY_LIST = [];
const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];
const RELATIONSHIPS = ["Child", "FosterChild", "Friend", "GrandChild", "GrandParent", "GreatGrandChild", "GreatGrandParent", "Parent", "Sibling", "Spouse"];

function dayOfYear(date) {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start + (start.getTimezoneOffset() - date.getTimezoneOffset()) * 60 * 1000;
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

export default function SharingTree() {
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [periods, setPeriods] = useState([]);
  const [householdsRaw, setHouseholds] = useState([]);
  const [reportRaw, setReport] = useState(null);
  const [dataScope, setDataScope] = useState("");
  const scope = `${year}|${user?.id || user?.email || ""}|${user?.role || ""}|${hiddenClientFields(user).slice().sort().join(",")}|${JSON.stringify(user?.page_permissions ?? null)}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const dataScopeRef = useRef("");
  const scopeMatch = dataScope === scope;
  const households = scopeMatch ? householdsRaw : EMPTY_LIST;
  const report = scopeMatch ? reportRaw : null;
  const [reportError, setReportError] = useState("");
  const loadSequence = useRef(0);
  const yearRef = useRef(year);
  yearRef.current = year;
  const saveVersion = useRef(0);
  const toyGuardRef = useRef({ isDirty: () => false, check: () => true });
  const distGuardRef = useRef({ isDirty: () => false, isPending: () => false, check: () => true });
  const [showSignup, setShowSignup] = useState(false);
  const [showMailer, setShowMailer] = useState(false);
  const [showPostcard, setShowPostcard] = useState(false);
  const [selectedHouseholds, setSelectedHouseholds] = useState(new Set());
  const [emailBatchRecipientIds, setEmailBatchRecipientIds] = useState([]);
  const [emailBatchOpen, setEmailBatchOpen] = useState(false);
  const [toyFollowupsOpen, setToyFollowupsOpen] = useState(false);
  const [duplicateClustersRaw, setDuplicateClusters] = useState([]);
  const duplicateClusters = dataScope === scope ? duplicateClustersRaw : EMPTY_LIST;
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [editingHousehold, setEditingHousehold] = useState(null);
  const [labelType, setLabelType] = useState(null);

  async function load() {
    const sequence = ++loadSequence.current;
    const loadScope = scope;
    setReport(null);
    setReportError("");
    try {
      const [{ data: hh }, { data: r }, { data: ps }, { data: dups }] = await Promise.all([
        api.get(`/sharing-tree/households?year=${year}&include_deleted=true`),
        api.get(`/sharing-tree/reports?year=${year}`),
        api.get("/sharing-tree/periods"),
        api.get(`/sharing-tree/duplicates?year=${year}`),
      ]);
      if (sequence !== loadSequence.current || scopeRef.current !== loadScope) return;
      dataScopeRef.current = loadScope;
      setDataScope(loadScope);
      setHouseholds(hh);
      setReport(r);
      setPeriods(ps);
      setDuplicateClusters(dups.clusters || []);
    } catch (error) {
      if (sequence === loadSequence.current && scopeRef.current === loadScope) setReportError(formatApiError(error));
    }
  }
  async function refreshReport() {
    const seq = loadSequence.current;
    const ver = saveVersion.current;
    const forYear = year;
    const forScope = scope;
    try {
      const { data: r } = await api.get(`/sharing-tree/reports?year=${forYear}`);
      if (forScope !== scopeRef.current || dataScopeRef.current !== forScope || seq !== loadSequence.current || ver !== saveVersion.current || yearRef.current !== forYear || r?.year !== forYear) return null;
      if (toyGuardRef.current.isDirty()) return r;
      setReport(r);
      return r;
    } catch { return null; }
  }
  function patchFreshToyRow(fresh) {
    if (dataScopeRef.current !== scopeRef.current) return;
    setReport((r) => (r && r.year === yearRef.current ? {
      ...r, toy_requests: (r.toy_requests || []).map((t) => (t.household_record_id === fresh.household_record_id && t.member_record_id === fresh.member_record_id ? { ...t, ...fresh } : t)),
      clothing_list: (r.clothing_list || []).map(t => (
        t.household_id === fresh.household_id && t.member_id === fresh.member_id && typeof fresh.gender === "string"
          ? { ...t, gender: fresh.gender } : t)),
    } : r));
  }
  function patchToyRow(savedYear, row, result) {
    if (yearRef.current !== savedYear || dataScopeRef.current !== scopeRef.current) return;
    saveVersion.current += 1;
    setReport((r) => (r && r.year === savedYear ? {
      ...r,
      toy_requests: (r.toy_requests || []).map((t) => (t.household_record_id === row.household_record_id && t.member_record_id === row.member_record_id
        ? { ...t, toy_items: result.toy_items, gift_request: result.gift_request, toy_request_token: result.toy_request_token,
          ...(typeof result.gender === "string" ? { gender: result.gender } : {}) } : t)),
      clothing_list: (r.clothing_list || []).map(t => (
        t.household_id === row.household_id && t.member_id === row.member_id && typeof result.gender === "string"
          ? { ...t, gender: result.gender } : t)),
    } : r));
  }
  function changeYear(next) {
    if (toyGuardRef.current.isDirty() && !window.confirm("You have an unsaved toy request edit. Discard it and change the program year?")) return;
    if (distGuardRef.current.isPending()) { window.alert("A bag count is still saving. Wait for it to finish before changing the program year."); return; }
    if (distGuardRef.current.isDirty() && !window.confirm("You have unsaved distribution bag counts. Discard them and change the program year?")) return;
    setYear(next);
  }
  useEffect(() => {
    load();
    setShowDuplicates(false);
    setLabelType(null);
    setSelectedHouseholds(new Set());
  }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps

  const periodYears = periods.map((p) => p.year).sort((a, b) => b - a);
  // activeYear = max program year on record (matches backend read-only guard)
  const activeYear = periodYears.length > 0 ? periodYears[0] : currentYear;
  const yearOptions = Array.from(new Set([...periodYears, currentYear])).sort((a, b) => b - a);
  const maxPeriodYear = activeYear;
  const staleYear = maxPeriodYear < currentYear && dayOfYear(new Date()) > 60;
  // Prior year = anything before the current active program year (not calendar year)
  const isPriorYear = year < activeYear;

  const currentPeriod = periods.find((p) => p.year === year) || null;
  const activeHouseholds = households.filter((h) => !h.deleted_at);
  const unremindedCount = activeHouseholds.filter((h) => !h.reminder_sent_at && h.client_email).length;
  const voucherTotal = report?.year === year ? foodVoucherTotal(report.voucher_list) : null;
  const canSendReminders = user?.role !== "viewer" && !!currentPeriod?.distribution_date;

  function openReminderBatch(ids) {
    setEmailBatchRecipientIds(ids);
    setEmailBatchOpen(true);
  }

  function toggleSelectedHousehold(id) {
    setSelectedHouseholds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllHouseholds(ids, selected) {
    setSelectedHouseholds(selected ? new Set(ids) : new Set());
  }

  return (
    <div className="space-y-6" data-testid="sharing-tree-page">
      <div className="rounded-2xl overflow-hidden relative" style={{ minHeight: 160 }}>
        <img src="https://images.pexels.com/photos/6995223/pexels-photo-6995223.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
             className="absolute inset-0 w-full h-full object-cover" alt="" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(5,47,102,0.92) 0%, rgba(5,47,102,0.5) 100%)" }} />
        <div className="relative z-10 p-5 sm:p-8 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs uppercase tracking-wider font-semibold">
            <TreePine className="w-3.5 h-3.5" /> Seasonal Program
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-3" style={{ fontFamily: "Outfit" }}>
            Sharing Tree {year}
          </h1>
          <p className="text-sm mt-2 max-w-xl opacity-90">
            Holiday gift program for client families. Toys for ages ≤9, gift cards for 10–18, and food vouchers for all.
          </p>
        </div>
      </div>

      {staleYear && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl fb-status-warning text-sm" data-testid="stale-year-banner">
          <CalendarClock className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <strong>Program year may need updating.</strong> The most recent program year on record is {maxPeriodYear}, but
            it's now {currentYear}. Go to <strong>Settings → Sharing Tree</strong> to configure the {currentYear} signup window.
          </div>
        </div>
      )}

      {isPriorYear && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl text-sm" style={{ background: "#F5F5F3", border: "1px solid var(--fb-border)" }} data-testid="prior-year-banner">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "var(--fb-text-muted)" }} />
          <span style={{ color: "var(--fb-text-muted)" }}>
            You are viewing <strong>{year}</strong> — a historical program year. This data is read-only; new signups cannot be recorded for past years.
          </span>
        </div>
      )}

      {duplicateClusters.length > 0 && (
        <div data-testid="duplicates-banner">
          <button
            onClick={() => setShowDuplicates((v) => !v)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-left"
            style={{ background: "#FFFBEB", border: "1px solid #FCD34D" }}
          >
            <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: "#B45309" }} />
            <span className="flex-1" style={{ color: "#92400E" }}>
              <strong>{duplicateClusters.length} possible duplicate{duplicateClusters.length !== 1 ? "s" : ""}</strong> found in the {year} signups.
              <span className="ml-1 underline cursor-pointer">{showDuplicates ? "Hide" : "Review"}</span>
            </span>
            {showDuplicates ? <ChevronUp className="w-4 h-4 shrink-0" style={{ color: "#B45309" }} /> : <ChevronDown className="w-4 h-4 shrink-0" style={{ color: "#B45309" }} />}
          </button>
          {showDuplicates && (
            <DuplicatesPanel
              clusters={duplicateClusters}
              year={year}
              onRefresh={load}
            />
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <label className="text-sm font-semibold">Program Year:</label>
          <select data-testid="year-select" value={year} onChange={(e) => changeYear(parseInt(e.target.value))}
                  className="px-4 py-2 rounded-lg border bg-white font-medium" style={{ borderColor: "var(--fb-border)" }}>
            {yearOptions.map((y) => <option key={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button type="button" data-testid="print-signup-mailer-btn" onClick={() => setShowMailer(true)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-xl border hover:bg-stone-50"
                  style={{ borderColor: "var(--fb-border)" }}>
            <Printer className="w-4 h-4" /> Print signup mailer
          </button>
          <button type="button" data-testid="print-postcard-mailer-btn" onClick={() => setShowPostcard(true)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-xl border hover:bg-stone-50"
                  style={{ borderColor: "var(--fb-border)" }}>
            <Printer className="w-4 h-4" /> Print postcard mailer
          </button>
          {canSendReminders && (
            <button data-testid="send-reminders-btn"
                    onClick={() => openReminderBatch(activeHouseholds.map((h) => h.id))}
                    disabled={unremindedCount === 0}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-xl border hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: "var(--fb-border)" }}>
              <Mail className="w-4 h-4" /> Send Pickup Reminders
            </button>
          )}
          {["admin", "staff"].includes(user?.role) && canUseStaffPage(user, "sharing-tree") && (
            <button type="button" data-testid="toy-followups-open-btn"
                    onClick={() => { if (toyGuardRef.current.check()) setToyFollowupsOpen(true); }}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-xl border hover:bg-stone-50"
                    style={{ borderColor: "var(--fb-border)" }}>
              <Mail className="w-4 h-4" /> Missing toy requests
            </button>
          )}
          {!isPriorYear && (
            <button data-testid="new-signup-btn" onClick={() => setShowSignup(true)}
              disabled={hasClientPrivacy(user)}
                    className="fb-btn-primary px-5 py-2.5 rounded-xl font-semibold inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Signup
            </button>
          )}
        </div>
      </div>

      <ToySuggestionCatalog canWrite={["admin", "staff"].includes(user?.role)} />
      {toyFollowupsOpen && ["admin", "staff"].includes(user?.role) && canUseStaffPage(user, "sharing-tree") &&
        <SharingTreeToyFollowups key={`toy-followups:${scope}`} year={year}
          onClose={() => setToyFollowupsOpen(false)} onComplete={() => refreshReport()} />}

      <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-3 sm:gap-4" data-testid="sharing-tree-summary">
        <KPI icon={UsersIcon} label="Households" value={report?.total_households} />
        <KPI icon={Gift} label="Toy Requests (≤9)" value={report?.toy_requests?.length} tone="accent" />
        <KPI icon={CreditCard} label="Gift Cards (10–18)" value={report?.gift_card_count} tone="success" />
        <KPI icon={UsersIcon} label="Adults (19–64)" value={report?.adult_count} />
        <KPI icon={UsersIcon} label="Seniors (65+)" value={report?.senior_count} />
        <KPI icon={CreditCard} label="Food Voucher Total" tone="success"
             value={voucherTotal ?? (reportError || report?.year === year ? "Unavailable" : undefined)}
             valueClassName="text-xl sm:text-2xl 2xl:text-xl"
             testId="food-voucher-total" />
      </div>

      {/* Voucher tier counts */}
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Food Voucher Tiers — Required Count</h3>
          {(report?.voucher_tier_counts || []).length > 0 && (
            <TierCountCsvButton tierCounts={report.voucher_tier_counts} year={year} />
          )}
        </div>
        <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] text-sm">
          <thead><tr style={{ background: "var(--fb-bg)" }}>
            <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Tier</th>
            <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Range</th>
            <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Amount</th>
            <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Vouchers Needed</th>
          </tr></thead>
          <tbody>
            {(report?.voucher_tier_counts || []).map((t, i) => (
              <tr key={i} className="border-t" style={{ borderColor: "var(--fb-border)" }}>
                <td className="px-4 py-3 font-semibold">{t.label || "Tier"}</td>
                <td className="px-4 py-3 tabular-nums">{t.min_size}–{t.max_size} ppl</td>
                <td className="px-4 py-3 tabular-nums">${t.amount}</td>
                <td className="px-4 py-3 text-right tabular-nums font-bold text-lg">{t.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <ToyRequestReview
        key={scope}
        year={year}
        rows={report?.year === year ? (report.toy_requests || []) : undefined}
        error={reportError}
        canEdit={["admin", "staff"].includes(user?.role) && !isPriorYear &&
          (user?.role === "admin" || user?.page_permissions == null || user.page_permissions.includes("sharing-tree"))}
        canEditGender={!hasClientPrivacy(user)}
        readOnlyReason={isPriorYear ? "Historical program year is read-only." : ""}
        guardRef={toyGuardRef}
        onSaved={patchToyRow}
        onReload={refreshReport}
        onRowReloaded={patchFreshToyRow}
        printButton={(guard) => (
          <button type="button" data-testid="print-labels-toys-btn" onClick={() => { if (guard()) setLabelType("toys"); }}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border hover:bg-stone-50" style={{ borderColor: "var(--fb-border)" }}>
            <Printer className="w-4 h-4" /> Print labels
          </button>
        )}
        exportControl={<CsvExportPanel listType="toys" year={year} data-testid="export-csv-btn" beforeExport={() => toyGuardRef.current.check()} />}
      />

      {/* Gift card list (ages 10–18) */}
      <GiftCardsByHousehold
        year={year}
        rows={report?.year === year ? report.gift_card_households : undefined}
        error={reportError}
        exportControl={<CsvExportPanel listType="giftcard_households" year={year} />}
      />
      <DistributionChecklist key={distributionScope(user, year)} year={year} user={user} guardRef={distGuardRef} />
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Gift Card List (10–18)</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" data-testid="print-labels-giftcards-btn" onClick={() => setLabelType("giftcards")}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border hover:bg-stone-50" style={{ borderColor: "var(--fb-border)" }}>
              <Printer className="w-4 h-4" /> Print labels
            </button>
            <CsvExportPanel listType="giftcards" year={year} data-testid="export-gc-csv-btn" />
          </div>
        </div>
        {(report?.gift_card_requests || []).length === 0 && (
          <div className="py-8 text-center text-sm" style={{ color: "var(--fb-text-muted)" }}>No gift card recipients recorded for {year}.</div>
        )}
        {(report?.gift_card_requests || []).length > 0 && (
          <ReportTableScroll label="Gift Card List (10–18) table">
          <table className="w-full min-w-[560px] text-sm">
            <thead><tr style={{ background: "var(--fb-bg)" }}>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Household ID</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Member ID</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Name</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Age</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Gender</th>
            </tr></thead>
            <tbody>
              {(report.gift_card_requests).map((t, i) => (
                <tr key={i} className="border-t" style={{ borderColor: "var(--fb-border)" }}>
                  <td className="px-4 py-3 font-mono text-xs">{t.household_id}</td>
                  <td className="px-4 py-3 font-mono text-xs">{t.member_id}</td>
                  <td className="px-4 py-3 font-medium">{t.name || "—"}</td>
                  <td className="px-4 py-3 tabular-nums">{t.age}</td>
                  <td className="px-4 py-3">{t.gender || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </ReportTableScroll>
        )}
      </div>

      {/* Food Voucher List */}
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Food Voucher List</h3>
          <CsvExportPanel listType="vouchers" year={year} data-testid="export-voucher-csv-btn" />
        </div>
        {(report?.voucher_list || []).length === 0 && (
          <div className="py-8 text-center text-sm" style={{ color: "var(--fb-text-muted)" }}>No voucher assignments for {year}.</div>
        )}
        {(report?.voucher_list || []).length > 0 && (
          <ReportTableScroll label="Food Voucher List table">
           <table className="w-full min-w-[700px] text-sm">
            <thead><tr style={{ background: "var(--fb-bg)" }}>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Household ID</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Contact Name</th>
               <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Address</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Members</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Amount</th>
            </tr></thead>
            <tbody>
              {(report.voucher_list).map((v, i) => (
                <tr key={i} className="border-t" style={{ borderColor: "var(--fb-border)" }}>
                  <td className="px-4 py-3 font-mono text-xs">{v.household_id}</td>
                  <td className="px-4 py-3 font-medium">{v.contact_name}</td>
                  <td className="px-4 py-3" style={{ overflowWrap: "anywhere" }}>{v.address || "—"}</td>
                  <td className="px-4 py-3 tabular-nums">{v.member_count}</td>
                  <td className="px-4 py-3 tabular-nums">{v.tier_amount ? `$${v.tier_amount}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </ReportTableScroll>
        )}
      </div>

      {/* Clothing List */}
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Clothing List (ages ≤9)</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" data-testid="print-labels-clothing-btn" onClick={() => setLabelType("clothing")}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border hover:bg-stone-50" style={{ borderColor: "var(--fb-border)" }}>
              <Printer className="w-4 h-4" /> Print labels
            </button>
            <CsvExportPanel listType="clothing" year={year} data-testid="export-clothing-csv-btn" />
          </div>
        </div>
        {(report?.clothing_list || []).length === 0 && (
          <div className="py-8 text-center text-sm" style={{ color: "var(--fb-text-muted)" }}>No clothing sizes recorded for {year}.</div>
        )}
        {(report?.clothing_list || []).length > 0 && (
          <ReportTableScroll label="Clothing List table">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr style={{ background: "var(--fb-bg)" }}>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Household ID</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Member ID</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Name</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Age</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Gender</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Pant Size</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Shirt Size</th>
              <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>PJ Size</th>
            </tr></thead>
            <tbody>
              {(report.clothing_list).map((c, i) => (
                <tr key={i} className="border-t" style={{ borderColor: "var(--fb-border)" }}>
                  <td className="px-4 py-3 font-mono text-xs">{c.household_id}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.member_id}</td>
                  <td className="px-4 py-3 font-medium">{c.name || "—"}</td>
                  <td className="px-4 py-3 tabular-nums">{c.age}</td>
                  <td className="px-4 py-3">{c.gender || "—"}</td>
                  <td className="px-4 py-3">{c.pant_size || "—"}</td>
                  <td className="px-4 py-3">{c.shirt_size || "—"}</td>
                  <td className="px-4 py-3">{c.pj_size || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </ReportTableScroll>
        )}
      </div>

      <EnrolledHouseholdsCard
        key={`enrolled-households:${scope}`}
        households={households}
        year={year}
        onRefresh={load}
        isPriorYear={isPriorYear}
        canEdit={user?.role !== "viewer"}
        canEditProfile={user?.role !== "viewer" && !hasClientPrivacy(user)}
        onEdit={setEditingHousehold}
        pickupSlots={currentPeriod?.pickup_slots || []}
        showFamilyItem={currentPeriod?.show_family_item || false}
        selectedIds={selectedHouseholds}
        onToggleSelected={toggleSelectedHousehold}
        onSelectAll={selectAllHouseholds}
        onSendSelected={(ids) => openReminderBatch(ids)}
        hiddenFields={hiddenClientFields(user)}
        exportControl={<CsvExportPanel listType="households" year={year} showFamilyItem={currentPeriod?.show_family_item || false} data-testid="export-households-csv-btn" />}
      />

      <ActivitySection
        targetType="sharing_tree_household,sharing_tree_year,sharing_tree_period"
        title="Recent Sharing Tree Activity"
        limit={40}
      />

      {showSignup && <SignupModal year={year} onClose={() => setShowSignup(false)} onCreated={() => { setShowSignup(false); load(); }} />}
      {labelType && <AveryLabelPrintModal key={`${scope}:${labelType}`} year={year} listType={labelType} onClose={() => setLabelType(null)} />}
      {showMailer && <SharingTreeMailer year={year} period={currentPeriod} onClose={() => setShowMailer(false)} />}
      {showPostcard && <SharingTreePostcard key={year} year={year} period={currentPeriod} onClose={() => setShowPostcard(false)} />}
      {editingHousehold && <EditHouseholdModal household={editingHousehold} onClose={() => setEditingHousehold(null)} onSaved={() => { setEditingHousehold(null); load(); }} />}

      {emailBatchOpen && currentPeriod && (
        <SharingTreeEmailBatchModal
          mode="pickup_reminder"
          year={year}
          recipientIds={emailBatchRecipientIds}
          period={currentPeriod}
          onClose={() => setEmailBatchOpen(false)}
          onComplete={(data) => {
            load();
            if (data.remaining === 0) setSelectedHouseholds(new Set());
          }}
        />
      )}
    </div>
  );
}

function HouseholdMiniCard({ hh }) {
  return (
    <div className="rounded-lg p-3 space-y-0.5 flex-1 min-w-0" style={{ background: "#F9FAFB", border: "1px solid var(--fb-border)" }}>
      <div className="font-semibold text-sm truncate">{hh.client_name || "Unknown"}</div>
      <div className="text-xs font-mono" style={{ color: "var(--fb-text-muted)" }}>{hh.anonymous_id}</div>
      <HouseholdContactDetails
        phone={hh.client_phone}
        email={hh.client_email}
        compact
        testId={`duplicate-household-contact-${hh.id}`}
      />
      {hh.client_address && (
        <div className="text-xs" style={{ color: "var(--fb-text-muted)", overflowWrap: "anywhere" }}>
          <span className="font-semibold">Address:</span> {hh.client_address}
        </div>
      )}
      <div className="text-xs" style={{ color: "var(--fb-text-muted)" }}>
        {hh.member_count} member{hh.member_count !== 1 ? "s" : ""}
        {hh.created_at && (
          <span className="ml-1">
            · {new Date(hh.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </span>
        )}
      </div>
      <div className="pt-1">
        <a
          href={`#household-${hh.id}`}
          className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
          style={{ color: "var(--fb-primary)" }}
          onClick={() => {
            setTimeout(() => {
              const el = document.getElementById(`household-${hh.id}`);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 50);
          }}
        >
          <ExternalLink className="w-3 h-3" /> Go to record
        </a>
      </div>
    </div>
  );
}

const MERGE_FIELDS = [
  { key: "phone",   label: "Phone",   getVal: (hh) => hh?.client_phone   || "" },
  { key: "email",   label: "Email",   getVal: (hh) => hh?.client_email   || "" },
  { key: "address", label: "Address", getVal: (hh) => hh?.client_address || "" },
  { key: "notes",   label: "Notes",   getVal: (hh) => hh?.notes          || "" },
];

function MergeDialog({ pair, hhMap, year, onClose, onMerged }) {
  const idA = pair.household_ids[0];
  const idB = pair.household_ids[1];
  const hhA = hhMap[idA];
  const hhB = hhMap[idB];

  const [survivorId, setSurvivorId] = useState(idA);
  const [choices, setChoices] = useState(() => {
    const init = {};
    MERGE_FIELDS.forEach(({ key, getVal }) => { init[key] = getVal(hhA); });
    return init;
  });
  const [saving, setSaving] = useState(false);

  function handleSurvivorChange(id) {
    const hh = hhMap[id];
    setSurvivorId(id);
    const init = {};
    MERGE_FIELDS.forEach(({ key, getVal }) => { init[key] = getVal(hh); });
    setChoices(init);
  }

  const sourceId = pair.household_ids.find((id) => id !== survivorId);

  async function handleConfirm() {
    setSaving(true);
    try {
      await api.post(`/sharing-tree/households/${sourceId}/merge-into/${survivorId}`, {
        source_token: hhMap[sourceId]?.roster_token,
        target_token: hhMap[survivorId]?.roster_token,
        preserve_both: true,
        chosen_phone:   choices.phone   !== undefined ? choices.phone   : null,
        chosen_email:   choices.email   !== undefined ? choices.email   : null,
        chosen_address: choices.address !== undefined ? choices.address : null,
        chosen_notes:   choices.notes   !== undefined ? choices.notes   : null,
      });
      onMerged();
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  const hhList = [{ hh: hhA, label: "A" }, { hh: hhB, label: "B" }];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }} onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
        data-testid="merge-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 flex items-center justify-between border-b" style={{ borderColor: "var(--fb-border)" }}>
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Merge Duplicate Households</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto max-h-[68vh]">
          {/* Survivor selector */}
          <div>
            <div className="text-sm font-semibold mb-2">Which record should survive?</div>
            <div className="grid grid-cols-2 gap-3">
              {hhList.map(({ hh, label }) => (
                <button
                  key={hh.id}
                  onClick={() => handleSurvivorChange(hh.id)}
                  data-testid={`survivor-${label.toLowerCase()}`}
                  className="p-3 rounded-xl border-2 text-left transition-colors"
                  style={{
                    borderColor: survivorId === hh.id ? "var(--fb-primary)" : "var(--fb-border)",
                    background: survivorId === hh.id ? "rgba(5,47,102,0.05)" : "#F9FAFB",
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-xs font-bold px-1.5 py-0.5 rounded"
                      style={{
                        background: survivorId === hh.id ? "var(--fb-primary)" : "#E5E7EB",
                        color: survivorId === hh.id ? "white" : "var(--fb-text-muted)",
                      }}
                    >
                      {label}
                    </span>
                    {survivorId === hh.id && (
                      <span className="text-xs font-semibold" style={{ color: "var(--fb-primary)" }}>Survives</span>
                    )}
                  </div>
                  <div className="font-semibold text-sm">{hh.client_name || "Unknown"}</div>
                  <div className="text-xs font-mono" style={{ color: "var(--fb-text-muted)" }}>{hh.anonymous_id}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--fb-text-muted)" }}>
                    {hh.member_count} member{hh.member_count !== 1 ? "s" : ""}
                  </div>
                </button>
              ))}
            </div>
            <p className="text-xs mt-2" style={{ color: "var(--fb-text-muted)" }}>
              All members from both records will be combined under the surviving record. The other record will be soft-deleted.
            </p>
            <div className="mt-3 text-xs space-y-2 rounded-lg border p-3">
              <p className="font-semibold">Family requests and additional information — both will be preserved</p>
              {hhList.map(({ hh, label }) => <div key={hh.id}>
                <strong>{label}: </strong>
                <span className="whitespace-pre-wrap break-words">Family item: {hh.family_item || "None"} · Additional information: {hh.additional_information || "None"}</span>
              </div>)}
              <p>Children stay separate. After merging, use “Combine duplicate members” only for confirmed duplicates.</p>
            </div>
          </div>

          {/* Field-level choices */}
          <div className="space-y-3">
            <div className="text-sm font-semibold">Field values — click a cell to select it</div>
            {MERGE_FIELDS.map(({ key, label, getVal }) => {
              const valA = getVal(hhA);
              const valB = getVal(hhB);
              const same = valA === valB;
              return (
                <div key={key} className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--fb-border)" }}>
                  <div
                    className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider flex items-center gap-2"
                    style={{ background: "var(--fb-bg)", color: "var(--fb-text-muted)", borderBottom: "1px solid var(--fb-border)" }}
                  >
                    {label}
                    {same
                      ? <span className="normal-case font-normal">— same on both records</span>
                      : <span className="normal-case font-medium" style={{ color: "#B45309" }}>— differs</span>}
                  </div>
                  {same ? (
                    <div className="px-3 py-2 text-sm">
                      {valA || <span style={{ color: "var(--fb-text-muted)" }}>—</span>}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2" style={{ borderTop: "none" }}>
                      {hhList.map(({ hh, label: lbl }) => {
                        const val = getVal(hh);
                        const selected = choices[key] === val;
                        return (
                          <button
                            key={hh.id}
                            data-testid={`field-choice-${key}-${lbl.toLowerCase()}`}
                            onClick={() => setChoices((c) => ({ ...c, [key]: val }))}
                            className="px-3 py-2.5 text-left text-sm transition-colors border-r last:border-r-0"
                            style={{
                              borderColor: "var(--fb-border)",
                              background: selected ? "rgba(5,47,102,0.07)" : "white",
                              outline: selected ? "2px solid var(--fb-primary)" : "none",
                              outlineOffset: "-2px",
                            }}
                          >
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="text-xs font-bold" style={{ color: "var(--fb-text-muted)" }}>{lbl}</span>
                              {selected && <span className="text-xs font-semibold" style={{ color: "var(--fb-primary)" }}>✓ Selected</span>}
                            </div>
                            <span style={{ color: val ? "inherit" : "var(--fb-text-muted)" }}>{val || "—"}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-3" style={{ borderColor: "var(--fb-border)" }}>
          <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--fb-border)" }}>
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            data-testid="confirm-merge-btn"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg font-semibold text-sm text-white disabled:opacity-50"
            style={{ background: "var(--fb-primary)" }}
          >
            <GitMerge className="w-4 h-4" />
            {saving ? "Merging…" : "Confirm Merge"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DuplicatesPanel({ clusters, year, onRefresh }) {
  const [dismissing, setDismissing] = useState({});
  const [mergePair, setMergePair] = useState(null); // { pair, hhMap }

  async function dismissPair(pair) {
    setDismissing((p) => ({ ...p, [pair.pair_key]: true }));
    try {
      await api.post("/sharing-tree/duplicates/dismiss", {
        household_id_a: pair.household_ids[0],
        household_id_b: pair.household_ids[1],
        year,
      });
      onRefresh();
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setDismissing((p) => ({ ...p, [pair.pair_key]: false }));
    }
  }

  const reasonLabel = (r) =>
    r === "same_client" ? "Same client record" : "Same name & address";

  return (
    <>
      <div className="mt-1 rounded-xl border overflow-hidden" style={{ borderColor: "#FCD34D" }} data-testid="duplicates-panel">
        <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ background: "#FFFBEB", color: "#92400E", borderBottom: "1px solid #FCD34D" }}>
          Possible Duplicate Signups — Review &amp; Dismiss
        </div>
        <div className="divide-y" style={{ background: "white", borderColor: "#FEF3C7" }}>
          {clusters.map((cluster, ci) => {
            const hhMap = Object.fromEntries(cluster.households.map((hh) => [hh.id, hh]));
            return (
              <div key={ci} className="p-4 space-y-3" data-testid="duplicate-cluster">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }}>
                  {reasonLabel(cluster.reason)}
                </span>

                {cluster.pairs.map((pair) => {
                  const [hhA, hhB] = pair.household_ids.map((id) => hhMap[id]).filter(Boolean);
                  if (!hhA || !hhB) return null;
                  return (
                    <div key={pair.pair_key} className="space-y-2" data-testid="duplicate-pair">
                      <div className="flex gap-2 items-stretch">
                        <HouseholdMiniCard hh={hhA} />
                        <div className="flex items-center text-xs font-bold shrink-0" style={{ color: "var(--fb-text-muted)" }}>↔</div>
                        <HouseholdMiniCard hh={hhB} />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setMergePair({ pair, hhMap })}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border hover:bg-blue-50 transition-colors"
                          style={{ borderColor: "var(--fb-primary)", color: "var(--fb-primary)" }}
                          data-testid="merge-duplicate-btn"
                        >
                          <GitMerge className="w-3.5 h-3.5" /> Merge
                        </button>
                        <button
                          onClick={() => dismissPair(pair)}
                          disabled={dismissing[pair.pair_key]}
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-50"
                          style={{ borderColor: "var(--fb-border)", color: "var(--fb-text-muted)" }}
                          data-testid="dismiss-duplicate-btn"
                        >
                          {dismissing[pair.pair_key] ? "Dismissing…" : "Not a duplicate"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {mergePair && (
        <MergeDialog
          pair={mergePair.pair}
          hhMap={mergePair.hhMap}
          year={year}
          onClose={() => setMergePair(null)}
          onMerged={() => { setMergePair(null); onRefresh(); }}
        />
      )}
    </>
  );
}

function KPI({ icon: Icon, label, value, tone, valueClassName = "text-3xl", testId }) {
  const colors = { accent: "var(--fb-accent)", success: "var(--fb-success)" };
  return (
    <div className="fb-card p-5 min-w-0" data-testid={testId}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
           style={{ background: `${colors[tone] || 'var(--fb-primary)'}15`, color: colors[tone] || 'var(--fb-primary)' }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className={`${valueClassName} font-bold tabular-nums break-words`} style={{ fontFamily: "Outfit" }}>{value ?? "—"}</div>
      <div className="text-xs uppercase tracking-wider font-semibold mt-1" style={{ color: "#8B918B" }}>{label}</div>
    </div>
  );
}

const LIST_COLUMNS = {
  households: [
    { key: "household_id", label: "Household ID" },
    { key: "contact_name", label: "Contact Name" },
    { key: "phone",        label: "Phone" },
    { key: "email",        label: "Email" },
    { key: "address",      label: "Address" },
    { key: "member_count", label: "Member Count" },
    { key: "child_count",  label: "Children Count" },
    { key: "senior_count", label: "Senior Count" },
  ],
  toys: [
    { key: "household_id", label: "Household ID" },
    { key: "member_id",    label: "Member ID" },
    { key: "name",         label: "Child Name" },
    { key: "age",          label: "Age" },
    { key: "gender",       label: "Gender" },
    { key: "gift_request", label: "Gift Request" },
    { key: "pant_size",    label: "Pant Size" },
    { key: "shirt_size",   label: "Shirt Size" },
    { key: "pj_size",      label: "PJ Size" },
  ],
  giftcards: [
    { key: "household_id", label: "Household ID" },
    { key: "member_id",    label: "Member ID" },
    { key: "name",         label: "Name" },
    { key: "age",          label: "Age" },
    { key: "gender",       label: "Gender" },
  ],
  giftcard_households: [
    { key: "household_id", label: "Household ID" },
    { key: "gift_card_count", label: "Gift Cards Needed" },
  ],
  vouchers: [
    { key: "household_id", label: "Household ID" },
    { key: "contact_name", label: "Contact Name" },
    { key: "phone",        label: "Phone" },
    { key: "address",      label: "Address" },
    { key: "member_count", label: "Member Count" },
    { key: "tier_amount",  label: "Voucher Amount" },
  ],
  clothing: [
    { key: "household_id", label: "Household ID" },
    { key: "member_id",    label: "Member ID" },
    { key: "name",         label: "Child Name" },
    { key: "age",          label: "Age" },
    { key: "gender",       label: "Gender" },
    { key: "pant_size",    label: "Pant Size" },
    { key: "shirt_size",   label: "Shirt Size" },
    { key: "pj_size",      label: "PJ Size" },
  ],
};

const SIZE_EXPORT_FIELDS = ["pant_size", "shirt_size", "pj_size"];
const ID_EXPORT_FIELDS = ["household_id", "member_id"];

export async function downloadListCsv(listType, fields, year, genderLabels = "standard", sizeLabels = "standard", idLabels = "standard") {
  const token = localStorage.getItem("fb_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const fieldsParam = encodeURIComponent(fields.join(","));
  const genderParam = fields.includes("gender") && genderLabels === "boy_girl"
    ? "&gender_labels=boy_girl" : "";
  const sizeParam = fields.some((field) => SIZE_EXPORT_FIELDS.includes(field)) && sizeLabels === "abbreviated"
    ? "&size_labels=abbreviated" : "";
  const idParam = fields.some((field) => ID_EXPORT_FIELDS.includes(field)) && idLabels === "short"
    ? "&id_labels=short" : "";
  const res = await fetch(
    `${API}/sharing-tree/export-csv?list_type=${listType}&fields=${fieldsParam}&year=${year}${genderParam}${sizeParam}${idParam}`,
    { credentials: "include", headers }
  );
  if (!res.ok) { alert("Export failed — please try again."); return; }
  const text = await res.text();
  const lines = text.split("\n");
  let csvText = text;
  if (lines.length > 1) {
    const headerCols = lines[0].split(",");
    const phoneIdx = headerCols.findIndex((h) => h.trim().toLowerCase() === "phone");
    if (phoneIdx >= 0) {
      const formatted = lines.map((line, i) => {
        if (i === 0 || !line.trim()) return line;
        const cols = line.split(",");
        if (cols[phoneIdx] !== undefined) {
          cols[phoneIdx] = formatPhone(cols[phoneIdx].trim());
        }
        return cols.join(",");
      });
      csvText = formatted.join("\n");
    }
  }
  // Fetch's text decoder strips the server BOM; restore exactly one for Excel.
  const blob = new Blob(["\ufeff", csvText.replace(/^\ufeff/, "")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sharing-tree-${listType}-${year}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function TierCountCsvButton({ tierCounts, year }) {
  function handleDownload() {
    const header = ["Tier Label", "Amount ($)", "Size Range", "Vouchers Needed"];
    const rows = tierCounts.map((t) => [
      t.label || "Tier",
      t.amount,
      `${t.min_size}–${t.max_size} people`,
      t.count,
    ]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sharing-tree-voucher-tiers-${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <button
      onClick={handleDownload}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border hover:bg-stone-50 transition-colors"
      style={{ borderColor: "var(--fb-border)", color: "var(--fb-text)" }}
      data-testid="export-tier-counts-csv-btn"
    >
      <FileDown className="w-3.5 h-3.5" /> Export CSV
    </button>
  );
}

export function CsvExportPanel({ listType, year, showFamilyItem = false, beforeExport }) {
  const baseCols = LIST_COLUMNS[listType] || [];
  const cols = (listType === "households" && showFamilyItem)
    ? [...baseCols, { key: "family_item", label: "Family Item" }]
    : baseCols;
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set(cols.map((c) => c.key)));
  const [busy, setBusy] = useState(false);
  const [useTagLabels, setUseTagLabels] = useState(false);
  const [useAbbreviatedSizes, setUseAbbreviatedSizes] = useState(false);
  const [useShortIds, setUseShortIds] = useState(true);
  const hasSelectedGender = cols.some((c) => c.key === "gender") && selected.has("gender");
  const hasSelectedSize = cols.some((c) => SIZE_EXPORT_FIELDS.includes(c.key) && selected.has(c.key));
  const hasSelectedId = cols.some((c) => ID_EXPORT_FIELDS.includes(c.key) && selected.has(c.key));

  useEffect(() => {
    const colKeys = cols.map((c) => c.key);
    setSelected((prev) => {
      const next = new Set(prev);
      colKeys.forEach((k) => next.add(k));
      for (const k of next) {
        if (!colKeys.includes(k)) next.delete(k);
      }
      return next;
    });
  }, [showFamilyItem, listType]);

  function toggle(key) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size === 1) return prev;
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  async function handleExport() {
    if (beforeExport && !beforeExport()) return;
    setBusy(true);
    const fields = cols.map((c) => c.key).filter((k) => selected.has(k));
    try {
      await downloadListCsv(
        listType, fields, year,
        hasSelectedGender && useTagLabels ? "boy_girl" : "standard",
        hasSelectedSize && useAbbreviatedSizes ? "abbreviated" : "standard",
        hasSelectedId && useShortIds ? "short" : "standard"
      );
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50"
        style={{ borderColor: "var(--fb-border)" }}
      >
        <FileDown className="w-4 h-4" /> Export CSV
      </button>
      {open && (
        <div
          className="absolute right-0 top-10 z-20 bg-white border rounded-xl shadow-lg p-4 w-56"
          style={{ borderColor: "var(--fb-border)" }}
        >
          <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--fb-text-muted)" }}>
            Select columns
          </div>
          <div className="space-y-2 mb-4">
            {cols.map((col) => (
              <label key={col.key} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={selected.has(col.key)}
                  onChange={() => toggle(col.key)}
                  disabled={selected.has(col.key) && selected.size === 1}
                />
                {col.label}
              </label>
            ))}
          </div>
          {hasSelectedGender && (
            <div className="mb-4 border-t pt-3" style={{ borderColor: "var(--fb-border)" }}>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={useTagLabels}
                  onChange={(e) => setUseTagLabels(e.target.checked)}
                />
                Use Boy/Girl labels
              </label>
              <p className="mt-1 text-xs" style={{ color: "var(--fb-text-muted)" }}>
                Export only: Male → Boy, Female → Girl. Saved genders and other values stay unchanged.
              </p>
            </div>
          )}
          {hasSelectedSize && (
            <div className="mb-4 border-t pt-3" style={{ borderColor: "var(--fb-border)" }}>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={useAbbreviatedSizes}
                  onChange={(e) => setUseAbbreviatedSizes(e.target.checked)}
                />
                Abbreviate clothing sizes
              </label>
              <p className="mt-1 text-xs" style={{ color: "var(--fb-text-muted)" }}>
                Export only: 4 - youth, XS - youth, M - adult, 0-3mo. Youth and adult stay spelled out; mo = months. Saved sizes stay unchanged.
              </p>
            </div>
          )}
          {hasSelectedId && (
            <div className="mb-4 border-t pt-3" style={{ borderColor: "var(--fb-border)" }}>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={useShortIds}
                  onChange={(e) => setUseShortIds(e.target.checked)}
                />
                Use short print IDs
              </label>
              <p className="mt-1 text-xs" style={{ color: "var(--fb-text-muted)" }}>
                H42 = household, M177 = member. Use only for {year}. Saved IDs stay unchanged.
                Unusual or conflicting IDs keep their full value.
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleExport}
              disabled={busy}
              className="flex-1 fb-btn-primary text-sm py-1.5 rounded-lg font-semibold"
            >
              {busy ? "Exporting…" : "Export"}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="px-3 py-1.5 text-sm rounded-lg border"
              style={{ borderColor: "var(--fb-border)" }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const MEMBER_FIELDS = ["name", "age", "gender", "toy_items", "toy_notes", "legacy_gift_request", "relationship", "pant_size", "shirt_size", "pj_size"];

function normalizeMember(c) {
  return {
    ...(c.id ? { id: c.id } : {}),
    name: c.name || "",
    age: c.age !== undefined ? String(c.age) : "",
    gender: c.gender || "",
    ...normalizeToyFields(c),
    relationship: c.relationship || "",
    pant_size: c.pant_size || "",
    shirt_size: c.shirt_size || "",
    pj_size: c.pj_size || "",
  };
}

const TOY_BLANK = { toy_items: [], toy_notes: "", legacy_gift_request: "" };

function LegacyRequestReview({ value, onClear, testId }) {
  if (!value) return null;
  return (
    <div className="text-xs rounded-lg p-2 flex items-start justify-between gap-2" style={{ background: "#FFFBEB", border: "1px solid #FCD34D", color: "#92400E" }} data-testid={testId}>
      <div>
        <div className="font-semibold">Original request (staff review only)</div>
        <div style={{ overflowWrap: "anywhere" }}>{value}</div>
        <div className="mt-0.5">Not used on labels. Choose items above, then clear this when reviewed.</div>
      </div>
      <button type="button" onClick={onClear} className="shrink-0 underline font-semibold">Clear original</button>
    </div>
  );
}

function sortMembersForDisplay(members = []) {
  const ageForSort = (member) => {
    if (member.age === undefined || member.age === null || member.age === "") return Number.NEGATIVE_INFINITY;
    const age = Number(member.age);
    return Number.isFinite(age) ? age : Number.NEGATIVE_INFINITY;
  };

  return [...members].sort((a, b) => {
    const aSpouse = (a.relationship || "").toLowerCase() === "spouse";
    const bSpouse = (b.relationship || "").toLowerCase() === "spouse";
    if (aSpouse !== bSpouse) return aSpouse ? -1 : 1;
    return ageForSort(b) - ageForSort(a);
  });
}

function computeChangeCount(currentMembers, currentNotes, initMembers, initNotes) {
  let count = 0;
  if (currentNotes !== initNotes) count++;
  count += Math.abs(currentMembers.length - initMembers.length);
  const minLen = Math.min(currentMembers.length, initMembers.length);
  for (let i = 0; i < minLen; i++) {
    for (const f of MEMBER_FIELDS) {
      const a = currentMembers[i][f];
      const b = initMembers[i][f];
      if (Array.isArray(a) || Array.isArray(b)) {
        if (JSON.stringify(a || []) !== JSON.stringify(b || [])) count++;
      } else if (a !== b) count++;
    }
  }
  return count;
}

export function EditHouseholdModal({ household, onClose, onSaved }) {
  const toySuggestions = useToySuggestions();
  const initMembers = useRef(sortMembersForDisplay(household.children || []).map(normalizeMember));
  const initNotes = useRef(household.notes || "");
  const initAddress = useRef(household.client_address || "");

  const [members, setMembers] = useState(
    sortMembersForDisplay(household.children || []).map(normalizeMember)
  );
  const [notes, setNotes] = useState(household.notes || "");
  const [additionalInformation, setAdditionalInformation] = useState(household.additional_information || "");
  const [address, setAddress] = useState(household.client_address || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const memberNotesCount = computeChangeCount(members, notes, initMembers.current, initNotes.current);
  const changeCount = memberNotesCount + (address !== initAddress.current ? 1 : 0)
    + (additionalInformation !== (household.additional_information || "") ? 1 : 0);

  function addMember() {
    setMembers([...members, { name: "", age: "", gender: "", ...TOY_BLANK, relationship: "", pant_size: "", shirt_size: "", pj_size: "" }]);
  }
  function removeMember(i) { setMembers(members.filter((_, j) => j !== i)); }
  function setMember(i, patch) {
    setMembers(members.map((x, j) => j === i ? { ...x, ...patch } : x));
  }

  async function submit() {
    setError(""); setSaving(true);
    try {
      const cleanMembers = members
        .filter((m) => m.age !== "" && m.age !== null)
        .map((m) => ({
          ...(m.id ? { id: m.id } : {}),
          name: m.name || "",
          age: parseInt(m.age) || 0,
          gender: m.gender || "",
          ...toyPayload(m),
          relationship: m.relationship || "",
          pant_size: m.pant_size || "",
          shirt_size: m.shirt_size || "",
          pj_size: m.pj_size || "",
        }));
      const { street, apt, city, state, zip } = parseAddress(address);
      await saveSharingTreeRoster("put", `/sharing-tree/households/${household.id}`, { members: cleanMembers, notes, address, street, apt, city, state, zip,
        expected_roster_token: household.roster_token,
        additional_information: additionalInformation });
      onSaved();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/40 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 p-6 sm:p-7 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-household-modal">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Edit Household</h3>
            <div className="text-sm mt-0.5" style={{ color: "var(--fb-text-muted)" }}>
              {household.client_name && <span className="font-medium">{household.client_name}</span>}
              {household.anonymous_id && <span className="ml-2 font-mono text-xs">#{household.anonymous_id}</span>}
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div
          className="rounded-xl p-4 mb-5"
          style={{ background: "var(--fb-bg)", border: "1px solid var(--fb-border)" }}
          data-testid="edit-household-contact"
        >
          <div className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: "var(--fb-text-muted)" }}>
            Primary contact on file
          </div>
          <HouseholdContactDetails phone={household.client_phone} email={household.client_email} />
        </div>

        <p className="text-xs mb-5" style={{ color: "var(--fb-text-muted)" }}>
          Ages 0–9 get a toy request + clothing sizes · Ages 10–18 get a gift card · Ages 19+ count toward household size only
        </p>

        {/* Address */}
        <div className="mb-5">
          <label className="block text-sm font-medium mb-1.5">Address</label>
          <AddressFields
            compact
            value={address}
            onChange={setAddress}
          />
        </div>

        {/* Members editor */}
        <div className="mb-4">
          <CombineSharingTreeMembers householdId={household.id} onCombined={onSaved} disabled={saving || changeCount > 0} />
          {changeCount > 0 && <p className="text-xs mt-1">Save or discard your changes before combining duplicate members.</p>}
          {(household.children || []).some(c => c.retired_member_ids?.length) && <p className="text-xs mt-2">
            Retired printed IDs: {(household.children || []).filter(c => c.retired_member_ids?.length).map(c =>
              `${c.retired_member_ids.join(", ")} → ${c.anonymous_child_id || c.id}`).join(" · ")}
          </p>}
        </div>
        <SharingTreeRosterReview householdId={household.id} onApplied={onSaved} disabled={saving || changeCount > 0} />
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium">Household Members</label>
            <button type="button" onClick={addMember} className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: "var(--fb-primary)" }}>
              <Plus className="w-3 h-3" /> Add member
            </button>
          </div>
          <div className="space-y-2">
            {members.length === 0 && (
              <div className="text-xs py-4 px-3 rounded-lg border" style={{ borderColor: "var(--fb-border)", color: "var(--fb-text-muted)" }}>
                No members. Use "Add member" to add household members.
              </div>
            )}
            {members.map((m, i) => {
              const ageNum = parseInt(m.age);
              const isToy = !isNaN(ageNum) && ageNum <= 9;
              const isGC = !isNaN(ageNum) && ageNum >= 10 && ageNum <= 18;
              const isAdult = !isNaN(ageNum) && ageNum >= 19;
              const tag = isToy ? "Toy" : isGC ? "Gift card" : isAdult ? "Adult" : "—";
              const tagBg = isToy ? "var(--fb-accent)" : isGC ? "var(--fb-success)" : "#8B918B";
              return (
                <div key={i} className="p-3 rounded-xl border space-y-2" style={{ borderColor: "var(--fb-border)" }} data-testid={`edit-member-${i}`}>
                  <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
                    <input placeholder="Name" value={m.name}
                           onChange={(e) => setMember(i, { name: e.target.value })}
                           className="col-span-3 md:col-span-4 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} />
                    <input type="number" placeholder="Age" min="0" max="120" value={m.age}
                           onChange={(e) => setMember(i, { age: e.target.value })}
                           className="col-span-1 md:col-span-2 px-2 py-2 rounded-lg border tabular-nums text-center" style={{ borderColor: "var(--fb-border)" }} />
                    <select value={m.gender} onChange={(e) => setMember(i, { gender: e.target.value })}
                            className="col-span-2 md:col-span-2 px-3 py-2 rounded-lg border bg-white" style={{ borderColor: "var(--fb-border)" }}>
                      <option value="">Gender</option>
                      {GENDERS.map((g) => <option key={g}>{g}</option>)}
                    </select>
                    <select value={m.relationship} onChange={(e) => setMember(i, { relationship: e.target.value })}
                            className="col-span-3 md:col-span-2 px-3 py-2 rounded-lg border bg-white" style={{ borderColor: "var(--fb-border)" }}>
                      <option value="">Relationship</option>
                      <option>Self</option>
                      {RELATIONSHIPS.map((r) => <option key={r}>{r}</option>)}
                    </select>
                    <div className="col-span-3 md:col-span-2 flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold px-2 py-1 rounded text-white" style={{ background: tagBg }}>{tag}</span>
                      <button type="button" onClick={() => removeMember(i)} className="p-1.5 rounded hover:bg-rose-50">
                        <Trash2 className="w-4 h-4" style={{ color: "var(--fb-error)" }} />
                      </button>
                    </div>
                  </div>
                  {isToy && (
                    <>
<ToyRequestField value={m.toy_items} onChange={(v) => setMember(i, { toy_items: v })}
  notes={m.toy_notes} onNotesChange={(v) => setMember(i, { toy_notes: v })}
  suggestions={toySuggestions.items} suggestionsLoading={toySuggestions.loading}
  suggestionsError={toySuggestions.error} onRetrySuggestions={toySuggestions.retry} testIdPrefix={`edit-toy-${i}`} />
<LegacyRequestReview value={m.legacy_gift_request} onClear={() => setMember(i, { legacy_gift_request: "" })} testId={`edit-legacy-${i}`} />
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div className="flex flex-col gap-0.5">
                          <label className="text-xs font-medium text-gray-600">Pant Size</label>
                          <select value={m.pant_size || ""} onChange={(e) => setMember(i, { pant_size: e.target.value })}
                                  className="px-2 py-2 rounded-lg border bg-white text-sm" style={{ borderColor: "var(--fb-border)" }}>
                            <option value="">— select —</option>
                            {CLOTHING_SIZES.map((s) => <option key={s}>{s}</option>)}
                          </select>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <label className="text-xs font-medium text-gray-600">Shirt Size</label>
                          <select value={m.shirt_size || ""} onChange={(e) => setMember(i, { shirt_size: e.target.value })}
                                  className="px-2 py-2 rounded-lg border bg-white text-sm" style={{ borderColor: "var(--fb-border)" }}>
                            <option value="">— select —</option>
                            {CLOTHING_SIZES.map((s) => <option key={s}>{s}</option>)}
                          </select>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <label className="text-xs font-medium text-gray-600">PJ Size</label>
                          <select value={m.pj_size || ""} onChange={(e) => setMember(i, { pj_size: e.target.value })}
                                  className="px-2 py-2 rounded-lg border bg-white text-sm" style={{ borderColor: "var(--fb-border)" }}>
                            <option value="">— select —</option>
                            {CLOTHING_SIZES.map((s) => <option key={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <SharingTreeAdditionalInformation value={additionalInformation}
          onChange={setAdditionalInformation} testIdPrefix="edit-household" audience="staff" />
        <div className="mb-5">
          <label className="block text-sm font-medium mb-1.5">Internal staff notes (not shared with clients)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Internal notes about this household…"
            className="w-full px-3.5 py-2.5 rounded-lg border resize-none"
            style={{ borderColor: "var(--fb-border)" }}
            data-testid="edit-household-notes"
          />
        </div>

        {error && <div className="fb-status-error text-sm p-3 rounded-lg mb-4" data-testid="edit-household-error">{error}</div>}

        <div className="flex items-center justify-between gap-3 pt-1 flex-wrap">
          <div data-testid="edit-household-change-count">
            {changeCount > 0 ? (
              <span
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
                style={{ background: "var(--fb-primary)", color: "#fff" }}
              >
                {changeCount} change{changeCount !== 1 ? "s" : ""}
              </span>
            ) : (
              <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>No changes yet</span>
            )}
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-lg border" style={{ borderColor: "var(--fb-border)" }}>Cancel</button>
            <button
              data-testid="edit-household-save"
              onClick={submit}
              disabled={saving || changeCount === 0}
              className="fb-btn-primary px-5 py-2.5 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SignupModal({ year, onClose, onCreated }) {
  const toySuggestions = useToySuggestions();
  const [clients, setClients] = useState([]);
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [clientId, setClientId] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [clientAddress, setClientAddress] = useState("");
  const [newHousehold, setNewHousehold] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    address: "", street: "", apt: "", city: "", state: "", zip: "",
    gender: "", age: "",
  });
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersLoadError, setMembersLoadError] = useState(false);
  const memberLoadVersion = useRef(0);
  const [familyItem, setFamilyItem] = useState("");
  const [rosterToken, setRosterToken] = useState(null);
  const [additionalInformation, setAdditionalInformation] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [period, setPeriod] = useState(null);
  const [createdData, setCreatedData] = useState(null); // set after successful signup
  const { user: signupUser } = useAuth();
  const canKiosk = canUseStaffPage(signupUser, "kiosk");
  const [kioskLoading, setKioskLoading] = useState(false);
  const [kioskMsg, setKioskMsg] = useState(null); // { ok, msg }
  const currentYear = new Date().getFullYear();

  useEffect(() => { (async () => {
    const [{ data: cs }, { data: pd }] = await Promise.all([
      api.get("/clients?limit=10000"),
      api.get(`/sharing-tree/period?year=${year}`).catch(() => ({ data: null })),
    ]);
    setClients(cs.clients);
    setPeriod(pd);
  })(); }, [year]);

  // When existing client selected, prefill members (primary + all household members)
  async function loadClientMembers(id) {
    const version = ++memberLoadVersion.current;
    setMembers([]);
    setFamilyItem("");
    setRosterToken(null);
    setAdditionalInformation("");
    setMembersLoadError(false);
    if (!id) { setClientAddress(""); setMembersLoading(false); return; }
    setMembersLoading(true);
    try {
    const [{ data: c }, { data: saved }] = await Promise.all([
      api.get(`/clients/${id}`),
      api.get(`/sharing-tree/signup-members?client_id=${encodeURIComponent(id)}&year=${year}`),
    ]);
    if (version !== memberLoadVersion.current) return;
    setRosterToken(saved.roster_token || null);
    setClientAddress(c.address || "");
    if (saved.members !== null) {
      setMembers(saved.members.map(normalizeMember));
      setFamilyItem(saved.family_item || "");
      setAdditionalInformation(saved.additional_information || "");
      return;
    }
    const primaryAge = c.birth_year ? currentYear - c.birth_year : "";
    const list = [
      { name: `${c.first_name} ${c.last_name}`.trim(), age: primaryAge, gender: c.gender || "", ...TOY_BLANK, relationship: "Self", pant_size: "", shirt_size: "", pj_size: "" },
      ...c.household_members.map((m) => ({
        name: `${m.first_name || ""} ${m.last_name || ""}`.trim(),
        age: m.birth_year ? currentYear - m.birth_year : "",
        gender: m.gender || "",
        ...TOY_BLANK,
        relationship: m.relationship || "",
        pant_size: "",
        shirt_size: "",
        pj_size: "",
      })),
    ];
    setMembers(list);
    } catch (e) {
      if (version === memberLoadVersion.current) {
        setMembersLoadError(true);
        setError(formatApiError(e));
      }
    } finally {
      if (version === memberLoadVersion.current) setMembersLoading(false);
    }
  }

  // Live duplicate check while typing in the New Household form
  useEffect(() => {
    if (mode !== "new") { setDuplicates([]); return; }
    const { first_name, last_name, phone, address } = newHousehold; // eslint-disable-line no-unused-vars
    if (!first_name && !last_name && !phone && !address) { setDuplicates([]); return; }
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ first_name, last_name, phone, address });
        const { data } = await api.get(`/clients-duplicates?${params}`);
        setDuplicates(data);
        if (data.length > 0) setShowDuplicates(true);
      } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [mode, newHousehold]);

  function addMember() {
    setMembers([...members, { name: "", age: "", gender: "", ...TOY_BLANK, relationship: "", pant_size: "", shirt_size: "", pj_size: "" }]);
  }
  function removeMember(i) { setMembers(members.filter((_, j) => j !== i)); }
  function setMember(i, patch) {
    setMembers(members.map((x, j) => j === i ? { ...x, ...patch } : x));
  }

  async function submit() {
    setError(""); setSaving(true);
    try {
      const cleanMembers = members.filter((m) => m.age !== "" && m.age !== null).map((m) => ({
        ...(m.id ? { id: m.id } : {}),
        name: m.name || "",
        age: parseInt(m.age) || 0,
        gender: m.gender || "",
        ...toyPayload(m),
        relationship: m.relationship || "",
        pant_size: m.pant_size || "",
        shirt_size: m.shirt_size || "",
        pj_size: m.pj_size || "",
      }));
      const payload = { year, members: cleanMembers, family_item: familyItem || "",
        additional_information: additionalInformation };
      if (mode === "existing") {
        if (!clientId) { setError("Select a client"); setSaving(false); return; }
        payload.client_id = clientId;
        payload.client_address = clientAddress;
      } else {
        const { age, ...rest } = newHousehold;
        if (!rest.first_name || !rest.last_name) {
          setError("First and last name are required for a new household");
          setSaving(false); return;
        }
        const ageNum = parseInt(age);
        const { street, apt, city, state, zip } = rest;
        payload.new_household = {
          first_name: rest.first_name,
          last_name: rest.last_name,
          phone: rest.phone,
          email: rest.email,
          address: rest.address,
          street,
          apt,
          city,
          state,
          zip,
          gender: rest.gender,
          birth_year: isNaN(ageNum) ? null : currentYear - ageNum,
        };
        // If primary not already in members, add them
        if (!cleanMembers.some((m) => (m.relationship || "").toLowerCase() === "self")) {
          payload.members = [
            { name: `${rest.first_name} ${rest.last_name}`.trim(), age: isNaN(ageNum) ? 30 : ageNum, gender: rest.gender, relationship: "Self", toy_items: [], toy_notes: "" },
            ...cleanMembers,
          ];
        }
      }
      const { data } = await saveSharingTreeRoster("post", "/sharing-tree/signup", { ...payload, expected_roster_token: rosterToken });
      setCreatedData(data);
      if (data.possible_duplicates && data.possible_duplicates.length > 0 && mode === "new") {
        // Notify staff so they can review (signup is already saved, but they may want to merge later)
        alert(`Signup saved. ${data.possible_duplicates.length} possible duplicate client(s) were found — review in the Clients list.`);
      }
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  }

  async function sendToKiosk() {
    if (!canKiosk) return;
    if (!createdData?.client_id) return;
    setKioskLoading(true);
    try {
      await api.put("/kiosk", { client_id: createdData.client_id, visible: true });
      setKioskMsg({ ok: true, msg: "Sent to kiosk!" });
    } catch (e) {
      setKioskMsg({ ok: false, msg: formatApiError(e) });
    } finally {
      setKioskLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/40 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 p-6 sm:p-7 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="signup-modal">
        <div className="flex items-start justify-between mb-6">
          <h3 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Sharing Tree Signup — {year}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {createdData && (
          <div className="space-y-4" data-testid="signup-success-panel">
            <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: "#ECFDF5", border: "1px solid #6EE7B7" }}>
              <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#059669" }} />
              <div>
                <div className="font-semibold" style={{ color: "#065F46" }}>Household enrolled!</div>
                <div className="text-sm mt-0.5" style={{ color: "#065F46" }}>
                  ID: <span className="font-mono">{createdData.anonymous_id}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {canKiosk && (createdData.client_id ? (
                <button
                  data-testid="signup-send-to-kiosk-btn"
                  onClick={sendToKiosk}
                  disabled={kioskLoading || kioskMsg?.ok}
                  className="inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-xl transition-colors disabled:opacity-50"
                  style={{
                    background: kioskMsg?.ok ? "var(--fb-success)" : "var(--fb-primary)",
                    color: "#fff",
                  }}
                >
                  <Monitor className="w-4 h-4" />
                  {kioskLoading ? "Sending…" : kioskMsg?.ok ? "Sent to Kiosk!" : "Send to Kiosk"}
                </button>
              ) : (
                <button
                  disabled
                  title="Only linked client records can be sent to the kiosk"
                  className="inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-xl opacity-40 cursor-not-allowed border"
                  style={{ borderColor: "var(--fb-border)", color: "var(--fb-text-muted)" }}
                  data-testid="signup-send-to-kiosk-disabled-btn"
                >
                  <Monitor className="w-4 h-4" />
                  Send to Kiosk
                </button>
              ))}
              <button
                data-testid="signup-done-btn"
                onClick={onCreated}
                className="inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-xl border hover:bg-stone-50"
                style={{ borderColor: "var(--fb-border)" }}
              >
                Done
              </button>
            </div>

            {kioskMsg && !kioskMsg.ok && (
              <div className="text-sm p-3 rounded-lg flex items-center gap-2" style={{ background: "#FEE2E2", color: "var(--fb-error)" }}>
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {kioskMsg.msg}
              </div>
            )}

            {!createdData.client_id && (
              <p className="text-xs" style={{ color: "var(--fb-text-muted)" }}>
                This household was signed up as a new record not yet linked to a client. Send to Kiosk is only available for linked client records.
              </p>
            )}
          </div>
        )}

        {!createdData && period && period.configured && !period.is_open && (
          <div className="mb-4 p-3 rounded-lg fb-status-warning flex items-start gap-2 text-sm" data-testid="staff-window-closed">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              Signup window for {year} is closed ({period.open_date} → {period.close_date}). Staff may still record signups on behalf of clients.
            </div>
          </div>
        )}
        {!createdData && period && !period.configured && (
          <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: "var(--fb-bg)", color: "var(--fb-text-muted)" }}>
            No signup window has been configured for {year}. Set one in <strong>Settings → Sharing Tree Signup Period</strong>.
          </div>
        )}

        {!createdData && period && period.configured && (
          <div className="mb-4">
            <DistributionCallout period={period} year={year} />
          </div>
        )}

        {!createdData && <div className="space-y-4">
          {/* Mode toggle */}
          <div className="inline-flex rounded-lg border p-0.5" style={{ borderColor: "var(--fb-border)" }}>
            <button
              data-testid="mode-existing"
              onClick={() => { setMode("existing"); setClientId(""); setClientSearch(""); loadClientMembers(""); }}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold ${mode === "existing" ? "text-white" : ""}`}
              style={{ background: mode === "existing" ? "var(--fb-primary)" : "transparent" }}
            >Existing Client</button>
            <button
              data-testid="mode-new"
              onClick={() => { setMode("new"); setClientId(""); setClientSearch(""); loadClientMembers(""); }}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold ${mode === "new" ? "text-white" : ""}`}
              style={{ background: mode === "new" ? "var(--fb-primary)" : "transparent" }}
            >New Household</button>
          </div>

          {mode === "existing" && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1.5">Client</label>
                {(() => {
                  const q = clientSearch.toLowerCase();
                  const filtered = q.length === 0 ? [] : clients.filter((c) => {
                    const name = `${c.first_name} ${c.last_name}`.toLowerCase();
                    const phone = (c.phone || "").toLowerCase();
                    return name.includes(q) || phone.includes(q);
                  }).slice(0, 20);
                  return (
                    <div className="relative">
                      <input
                        data-testid="signup-client-search"
                        placeholder="Search by name or phone…"
                        value={clientSearch}
                        autoComplete="off"
                        onChange={(e) => {
                          setClientSearch(e.target.value);
                          if (!e.target.value) { setClientId(""); loadClientMembers(""); }
                        }}
                        onFocus={() => setClientSearchOpen(true)}
                        onBlur={() => setTimeout(() => setClientSearchOpen(false), 150)}
                        className="w-full px-3.5 py-2.5 rounded-lg border bg-white pr-24"
                        style={{ borderColor: clientId ? "var(--fb-primary)" : "var(--fb-border)" }}
                      />
                      {clientId && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold" style={{ color: "var(--fb-success)" }}>
                          ✓ Selected
                        </span>
                      )}
                      {clientSearchOpen && filtered.length > 0 && (
                        <div className="absolute z-30 mt-1 w-full rounded-lg border bg-white shadow-lg max-h-56 overflow-y-auto" style={{ borderColor: "var(--fb-border)" }}>
                          {filtered.map((c) => (
                            <button key={c.id} type="button"
                              onMouseDown={() => {
                                setClientId(c.id);
                                setClientSearch(`${c.first_name} ${c.last_name}`);
                                setClientSearchOpen(false);
                                loadClientMembers(c.id);
                              }}
                              className="w-full text-left px-4 py-2.5 hover:bg-stone-50 border-b last:border-b-0 text-sm"
                              style={{ borderColor: "var(--fb-border)" }}>
                              <span className="font-medium">{c.first_name} {c.last_name}</span>
                              {c.phone && <span className="ml-2 text-xs" style={{ color: "var(--fb-text-muted)" }}>{formatPhone(c.phone)}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
                <p className="text-xs mt-1.5" style={{ color: "var(--fb-text-muted)" }}>
                  {clientId
                    ? "Household members have been prepopulated below."
                    : clientSearch.length > 0
                      ? "No matching clients found."
                      : "Type a name or phone number to search — household members will prepopulate on selection."}
                </p>
              </div>
              {clientId && (
                <div>
                  <label className="block text-sm font-medium mb-1.5">Address</label>
                  <input
                    value={clientAddress}
                    onChange={(e) => setClientAddress(e.target.value)}
                    placeholder="Street address"
                    className="w-full px-3.5 py-2.5 rounded-lg border bg-white"
                    style={{ borderColor: "var(--fb-border)" }}
                    data-testid="signup-client-address"
                  />
                  <p className="text-xs mt-1" style={{ color: "var(--fb-text-muted)" }}>Changes will be saved to the client's record.</p>
                </div>
              )}
            </div>
          )}

          {mode === "new" && (
            <div className="space-y-3 p-4 rounded-xl border" style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}>
              <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>New Household — Primary Contact</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input placeholder="First name *" value={newHousehold.first_name}
                       onChange={(e) => setNewHousehold({ ...newHousehold, first_name: e.target.value })}
                       className="px-3 py-2 rounded-lg border bg-white" style={{ borderColor: "var(--fb-border)" }}
                       data-testid="new-household-firstname" />
                <input placeholder="Last name *" value={newHousehold.last_name}
                       onChange={(e) => setNewHousehold({ ...newHousehold, last_name: e.target.value })}
                       className="px-3 py-2 rounded-lg border bg-white" style={{ borderColor: "var(--fb-border)" }}
                       data-testid="new-household-lastname" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select value={newHousehold.gender} onChange={(e) => setNewHousehold({ ...newHousehold, gender: e.target.value })}
                        className="px-3 py-2 rounded-lg border bg-white" style={{ borderColor: "var(--fb-border)" }}>
                  <option value="">Gender</option>
                  {GENDERS.map((g) => <option key={g}>{g}</option>)}
                </select>
                <input type="number" min="0" max="120" placeholder="Age" value={newHousehold.age}
                       onChange={(e) => setNewHousehold({ ...newHousehold, age: e.target.value })}
                       className="px-3 py-2 rounded-lg border bg-white tabular-nums" style={{ borderColor: "var(--fb-border)" }} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input placeholder="Phone" value={newHousehold.phone}
                       onChange={(e) => setNewHousehold({ ...newHousehold, phone: e.target.value })}
                       className="px-3 py-2 rounded-lg border bg-white" style={{ borderColor: "var(--fb-border)" }} />
                <input placeholder="Email" value={newHousehold.email}
                       onChange={(e) => setNewHousehold({ ...newHousehold, email: e.target.value })}
                       className="px-3 py-2 rounded-lg border bg-white" style={{ borderColor: "var(--fb-border)" }} />
              </div>
              <AddressFields
                compact
                value={newHousehold.address}
                onChange={(v) => {
                  const { street, apt, city, state, zip } = parseAddress(v);
                  setNewHousehold({ ...newHousehold, address: v, street, apt, city, state, zip });
                }}
              />

              {showDuplicates && duplicates.length > 0 && (
                <div className="rounded-lg border p-3 fb-status-warning" data-testid="duplicates-panel">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">Possible duplicate{duplicates.length > 1 ? "s" : ""} found ({duplicates.length}):</div>
                      <ul className="mt-2 space-y-2">
                        {duplicates.map((d) => (
                          <li key={d.id} className="flex items-start justify-between gap-3 p-2 rounded bg-white">
                            <div className="text-xs">
                              <div className="font-semibold">{d.first_name} {d.last_name}</div>
                              <div style={{ color: "var(--fb-text-muted)" }}>{[d.address, d.phone ? formatPhone(d.phone) : null].filter(Boolean).join(" · ")}</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => { setMode("existing"); setClientId(d.id); setClientSearch(`${d.first_name} ${d.last_name}`); loadClientMembers(d.id); }}
                              className="text-xs font-semibold px-3 py-1.5 rounded fb-btn-primary text-white"
                              data-testid={`use-duplicate-${d.id}`}
                            >
                              Use this client
                            </button>
                          </li>
                        ))}
                      </ul>
                      <button type="button" onClick={() => setShowDuplicates(false)}
                              className="text-xs font-medium mt-2 underline">Dismiss — create new anyway</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {period?.show_family_item && (
            <div>
              <label className="block text-sm font-medium mb-1.5">Family item <span className="font-normal" style={{ color: "var(--fb-text-muted)" }}>(optional)</span></label>
              <input
                placeholder="e.g. Family game, Deck of cards"
                value={familyItem}
                onChange={(e) => setFamilyItem(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg border bg-white"
                style={{ borderColor: "var(--fb-border)" }}
                data-testid="signup-family-item"
              />
            </div>
          )}

          {/* Members editor */}
          <SharingTreeAdditionalInformation value={additionalInformation}
            onChange={setAdditionalInformation} testIdPrefix="signup" audience="staff" />
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Household Members</label>
              <button type="button" onClick={addMember} className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: "var(--fb-primary)" }}>
                <Plus className="w-3 h-3" /> Add member
              </button>
            </div>
            <p className="text-xs mb-3" style={{ color: "var(--fb-text-muted)" }}>
              Ages 0–9 get a toy request + clothing sizes · Ages 10–18 get a gift card (anonymous) · Ages 19+ count toward household size only
            </p>

            <div className="space-y-2">
              {members.length === 0 && (
                <div className="text-xs py-4 px-3 rounded-lg border" style={{ borderColor: "var(--fb-border)", color: "var(--fb-text-muted)" }}>
                  No members yet. {mode === "existing" ? "Pick a client to prepopulate." : "Add household members above."}
                </div>
              )}
              {members.map((m, i) => {
                const ageNum = parseInt(m.age);
                const isToy = !isNaN(ageNum) && ageNum <= 9;
                const isGC = !isNaN(ageNum) && ageNum >= 10 && ageNum <= 18;
                const isAdult = !isNaN(ageNum) && ageNum >= 19;
                const tag = isToy ? "Toy" : isGC ? "Gift card" : isAdult ? "Adult" : "—";
                const tagBg = isToy ? "var(--fb-accent)" : isGC ? "var(--fb-success)" : "#8B918B";
                return (
                  <div key={i} className="p-3 rounded-xl border space-y-2" style={{ borderColor: "var(--fb-border)" }} data-testid={`signup-member-${i}`}>
                    <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
                      <input placeholder="Name" value={m.name}
                             onChange={(e) => setMember(i, { name: e.target.value })}
                             className="col-span-3 md:col-span-4 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} />
                      <input type="number" placeholder="Age" min="0" max="120" value={m.age === "" ? "" : m.age}
                             onChange={(e) => setMember(i, { age: e.target.value })}
                             className="col-span-1 md:col-span-2 px-2 py-2 rounded-lg border tabular-nums text-center" style={{ borderColor: "var(--fb-border)" }} />
                      <select value={m.gender} onChange={(e) => setMember(i, { gender: e.target.value })}
                              className="col-span-2 md:col-span-2 px-3 py-2 rounded-lg border bg-white" style={{ borderColor: "var(--fb-border)" }}>
                        <option value="">Gender</option>
                        {GENDERS.map((g) => <option key={g}>{g}</option>)}
                      </select>
                      <select value={m.relationship} onChange={(e) => setMember(i, { relationship: e.target.value })}
                              className="col-span-3 md:col-span-2 px-3 py-2 rounded-lg border bg-white" style={{ borderColor: "var(--fb-border)" }}>
                        <option value="">Relationship</option>
                        <option>Self</option>
                        {RELATIONSHIPS.map((r) => <option key={r}>{r}</option>)}
                      </select>
                      <div className="col-span-3 md:col-span-2 flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold px-2 py-1 rounded text-white" style={{ background: tagBg }}>{tag}</span>
                        <button type="button" onClick={() => removeMember(i)} className="p-1.5 rounded hover:bg-rose-50">
                          <Trash2 className="w-4 h-4" style={{ color: "var(--fb-error)" }} />
                        </button>
                      </div>
                    </div>
                    {isToy && (
                      <>
<ToyRequestField value={m.toy_items} onChange={(v) => setMember(i, { toy_items: v })}
  notes={m.toy_notes} onNotesChange={(v) => setMember(i, { toy_notes: v })}
  suggestions={toySuggestions.items} suggestionsLoading={toySuggestions.loading}
  suggestionsError={toySuggestions.error} onRetrySuggestions={toySuggestions.retry} testIdPrefix={`signup-toy-${i}`} />
<LegacyRequestReview value={m.legacy_gift_request} onClear={() => setMember(i, { legacy_gift_request: "" })} testId={`signup-legacy-${i}`} />
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <div className="flex flex-col gap-0.5">
                            <label className="text-xs font-medium text-gray-600">Pant Size</label>
                            <select value={m.pant_size || ""} onChange={(e) => setMember(i, { pant_size: e.target.value })}
                                    className="px-2 py-2 rounded-lg border bg-white text-sm" style={{ borderColor: "var(--fb-border)" }}>
                              <option value="">— select —</option>
                              {CLOTHING_SIZES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                          </div>
                          <div className="flex flex-col gap-0.5">
                            <label className="text-xs font-medium text-gray-600">Shirt Size</label>
                            <select value={m.shirt_size || ""} onChange={(e) => setMember(i, { shirt_size: e.target.value })}
                                    className="px-2 py-2 rounded-lg border bg-white text-sm" style={{ borderColor: "var(--fb-border)" }}>
                              <option value="">— select —</option>
                              {CLOTHING_SIZES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                          </div>
                          <div className="flex flex-col gap-0.5">
                            <label className="text-xs font-medium text-gray-600">PJ Size</label>
                            <select value={m.pj_size || ""} onChange={(e) => setMember(i, { pj_size: e.target.value })}
                                    className="px-2 py-2 rounded-lg border bg-white text-sm" style={{ borderColor: "var(--fb-border)" }}>
                              <option value="">— select —</option>
                              {CLOTHING_SIZES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {error && <div className="fb-status-error text-sm p-3 rounded-lg" data-testid="signup-error">{error}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-lg border" style={{ borderColor: "var(--fb-border)" }}>Cancel</button>
            <button data-testid="signup-submit-btn" onClick={submit} disabled={saving || membersLoading || membersLoadError}
                    className="fb-btn-primary px-5 py-2.5 rounded-lg font-semibold">
              {saving ? "Saving…" : "Submit Signup"}
            </button>
          </div>
        </div>}
      </div>
    </div>
  );
}
